import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import "katex/dist/katex.min.css";
import { decodeLatexEntities, resolveImageSrc } from "./markdownHelpers";

// Single source of truth for math + markdown rendering across the app
// (chat answers, source previews, knowledge-search result bodies). Renders
// inline/block LaTeX ($...$, $$...$$) via KaTeX and rewrites /api/v1/images/...
// markdown images to absolute backend URLs.

function CustomImage({ src, alt, ...props }) {
  return (
    <img
      src={resolveImageSrc(src)}
      alt={alt || ""}
      style={{
        maxWidth: "100%",
        height: "auto",
        margin: "8px 0",
        borderRadius: "4px",
      }}
      {...props}
    />
  );
}

// dir="auto": per-element content-based bidi detection, applied to every
// text-bearing block tag react-markdown can render. No per-instance
// lang/text-direction config exists (or should need to exist) to key off
// instead — this makes any non-Latin-script workspace, not just this one,
// render with correct glyph/punctuation order automatically, and correctly
// handles a single answer that mixes RTL and LTR paragraphs (e.g. an Arabic
// answer quoting a Latin-script decree number). Implemented as per-tag
// component overrides rather than one wrapping <div dir="auto"> around the
// whole render so no extra DOM node is introduced — this is the single
// shared renderer used app-wide (chat answers, source previews, search
// results per the header comment above), so an added wrapper here could
// silently change layout for every existing consumer/workspace.
const BIDI_BLOCK_TAGS = ["p", "li", "td", "th", "blockquote", "h1", "h2", "h3", "h4", "h5", "h6"];

function withAutoDir(Tag) {
  return function AutoDirBlock(props) {
    return <Tag {...props} dir="auto" />;
  };
}

// Everything rendered here is untrusted: OCR'd document text from any
// uploader/connector and LLM answers (which can echo injected document text).
// Raw HTML is parsed (answers use <mark>, <sub>, <br> ...) and then sanitized
// with GitHub's allow-list, so nothing that can run script, load frames/plugins,
// submit forms or restyle the page reaches the DOM. Math survives as the
// remark-math code nodes (their classes are allowed) and is typeset by KaTeX
// AFTER sanitizing, so KaTeX's own markup is never stripped.
export const SANITIZE_SCHEMA = {
  ...defaultSchema,
  tagNames: [...defaultSchema.tagNames, "mark"],
  attributes: {
    ...defaultSchema.attributes,
    code: [["className", /^language-./, "math-inline", "math-display"]],
    // Inline [n] citation markers (utils/citations.js) — one fixed class only.
    sup: [["className", "cite-ref"]],
  },
};

// The one rehype chain for untrusted markdown: raw HTML -> sanitize -> KaTeX.
export function buildRehypePlugins(katexOptions = {}) {
  return [
    rehypeRaw,
    [rehypeSanitize, SANITIZE_SCHEMA],
    [rehypeKatex, { throwOnError: false, strict: false, output: "html", ...katexOptions }],
  ];
}

export const MARKDOWN_PROPS = {
  remarkPlugins: [remarkGfm, remarkMath],
  rehypePlugins: buildRehypePlugins({ displayMode: true }),
  components: {
    img: CustomImage,
    ...Object.fromEntries(BIDI_BLOCK_TAGS.map((tag) => [tag, withAutoDir(tag)])),
  },
};

export default function MarkdownView({ children }) {
  return (
    <ReactMarkdown {...MARKDOWN_PROPS}>
      {decodeLatexEntities(children || "")}
    </ReactMarkdown>
  );
}
